import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    DatePicker,
    Statistic,
    Typography,
    Space,
    Spin
} from 'antd';

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';

import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getData } from '../api/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const Dashboard = ({ isAdmin, isMerchant }) => {
    const { t } = useTranslation();
    const getSevenDaysBefore = () => dayjs().subtract(7, 'day');

    const [dateRange, setDateRange] = useState([getSevenDaysBefore(), dayjs()]);
    const [loading, setLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        totalTransactions: 0,
        successfulTransactions: 0,
        successfulPercentage: 0,
        failedTransactions: 0,
        failedPercentage: 0,
        totalAmount: 0,
        successfulAmount: 0,
        failedAmount: 0
    });
    const [transactionChartData, setTransactionChartData] = useState([]);
    const [amountChartData, setAmountChartData] = useState([]);

    const pieDataCount = [
        { name: t('dashboard.successful'), value: dashboardData.successfulTransactions, color: '#52c41a' },
        { name: t('dashboard.failed'), value: dashboardData.failedTransactions, color: '#ff4d4f' }
    ];

    const pieDataAmount = [
        { name: t('dashboard.successful'), value: dashboardData.successfulAmount, color: '#52c41a' },
        { name: t('dashboard.failed'), value: dashboardData.failedAmount, color: '#ff4d4f' }
    ];

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const params = {
                fromDate: dateRange[0].format('DD.MM.YYYY'),
                toDate: dateRange[1].format('DD.MM.YYYY')
            };
            const url = isAdmin
                ? '/admin/dashboard/transaction-general-info'
                : isMerchant
                    ? '/merchant/dashboard/transaction-general-info'
                    : '/merchant-user/dashboard/transaction-general-info';

            const response = await getData(url, params);
            const data = response?.data?.data;

            if (data) {
                setDashboardData({
                    totalTransactions: data.totalTransactionCount || 0,
                    successfulTransactions: data.successCount || 0,
                    successfulPercentage: data.successCountPercentage || 0,
                    failedTransactions: data.cancelCount || 0,
                    failedPercentage: data.failedCountPercentage || 0,
                    totalAmount: data.totalTransactionSum || 0,
                    successfulAmount: data.successSum || 0,
                    failedAmount: data.cancelSum || 0
                });

                if (data.charts) {
                    setTransactionChartData(data.charts.map(item => ({
                        date: item.date,
                        [t('dashboard.total')]: item.allCount,
                        [t('dashboard.successful')]: item.successCount,
                        [t('dashboard.failed')]: item.failedCount
                    })));

                    setAmountChartData(data.charts.map(item => ({
                        date: item.date,
                        [t('dashboard.total')]: item.allSumm,
                        [t('dashboard.successful')]: item.successSumm,
                        [t('dashboard.failed')]: item.failedSumm
                    })));
                } else {
                    setTransactionChartData([]);
                    setAmountChartData([]);
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [dateRange]);

    const handleDateChange = (dates) => {
        if (dates?.[0]?.isValid() && dates?.[1]?.isValid()) {
            setDateRange(dates);
        }
    };

    const formatNumber = (num) => new Intl.NumberFormat('uz-UZ').format(num) + ` ${t('dashboard.unitCount')}`;
    const formatCurrency = (amount) => new Intl.NumberFormat('uz-UZ').format(amount / 100) + ` ${t('dashboard.unitCurrency')}`;

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>{t('dashboard.title')}</Title>

            <Row style={{ marginBottom: '24px' }}>
                <Col>
                    <Space>
                        <span>{t('dashboard.dateRange')}</span>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateChange}
                            format="DD.MM.YYYY"
                            placeholder={[t('dashboard.startDate'), t('dashboard.endDate')]}
                        />
                    </Space>
                </Col>
            </Row>

            {loading && (
                <Row justify="center" style={{ marginBottom: '24px' }}>
                    <Spin size="large" />
                </Row>
            )}

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ backgroundColor: '#1890ff', color: 'white' }}>
                        <Statistic
                            title={<span style={{ color: 'white' }}>{t('dashboard.totalTransactions')}</span>}
                            value={formatNumber(dashboardData.totalTransactions)}
                            valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ backgroundColor: '#52c41a', color: 'white' }}>
                        <Statistic
                            title={<span style={{ color: 'white' }}>{t('dashboard.successfulTransactions')}</span>}
                            value={formatNumber(dashboardData.successfulTransactions)}
                            valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                        />
                        <div style={{ color: 'white', fontSize: '14px' }}>({dashboardData.successfulPercentage}%)</div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ backgroundColor: '#ff4d4f', color: 'white' }}>
                        <Statistic
                            title={<span style={{ color: 'white' }}>{t('dashboard.failedTransactions')}</span>}
                            value={formatNumber(dashboardData.failedTransactions)}
                            valueStyle={{ color: 'white', fontSize: '32px', fontWeight: 'bold' }}
                        />
                        <div style={{ color: 'white', fontSize: '14px' }}>({dashboardData.failedPercentage}%)</div>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ backgroundColor: '#722ed1', color: 'white' }}>
                        <Statistic
                            title={<span style={{ color: 'white' }}>{t('dashboard.totalAmount')}</span>}
                            value={formatCurrency(dashboardData.totalAmount)}
                            valueStyle={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ backgroundColor: '#13c2c2', color: 'white' }}>
                        <Statistic
                            title={<span style={{ color: 'white' }}>{t('dashboard.successfulAmount')}</span>}
                            value={formatCurrency(dashboardData.successfulAmount)}
                            valueStyle={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <Card style={{ backgroundColor: '#fa8c16', color: 'white' }}>
                        <Statistic
                            title={<span style={{ color: 'white' }}>{t('dashboard.failedAmount')}</span>}
                            value={formatCurrency(dashboardData.failedAmount)}
                            valueStyle={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} lg={12}>
                    <Card title={t('dashboard.chartByCount')}>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={transactionChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip formatter={formatNumber} />
                                <Legend />
                                <Line type="monotone" dataKey={t('dashboard.total')} stroke="#1890ff" name={t('dashboard.total')} strokeWidth={2} />
                                <Line type="monotone" dataKey={t('dashboard.successful')} stroke="#52c41a" name={t('dashboard.successful')} strokeWidth={2} />
                                <Line type="monotone" dataKey={t('dashboard.failed')} stroke="#ff4d4f" name={t('dashboard.failed')} strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title={t('dashboard.chartByAmount')}>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={amountChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis tickFormatter={value => (value / 1000000).toFixed(0) + 'M'} />
                                <Tooltip formatter={formatCurrency} />
                                <Legend />
                                <Line type="monotone" dataKey={t('dashboard.total')} stroke="#1890ff" name={t('dashboard.total')} strokeWidth={2} />
                                <Line type="monotone" dataKey={t('dashboard.successful')} stroke="#52c41a" name={t('dashboard.successful')} strokeWidth={2} />
                                <Line type="monotone" dataKey={t('dashboard.failed')} stroke="#ff4d4f" name={t('dashboard.failed')} strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card title={t('dashboard.chartPieCount')}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieDataCount}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                >
                                    {pieDataCount.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={formatNumber} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title={t('dashboard.chartPieAmount')}>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={pieDataAmount}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                >
                                    {pieDataAmount.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={formatCurrency} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;